import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkle, X as XIcon, ArrowRight, ArrowLeft, Rocket,
  CurrencyDollar, Truck, ChartLineUp, Share,
} from "@phosphor-icons/react";
import client from "../lib/api";

/*
  OnboardingTour — first-login product walkthrough.
  Non-blocking corner card. Fires automatically from Dashboard on first visit
  for users < 30 days old whose tour isn't marked completed or dismissed.

  Design: Lightweight card in bottom-right (bottom on mobile). Doesn't block
  the UI so user can still explore while touring. Progress dots + Prev/Next.
  Escape key dismisses. Left/Right arrows navigate.

  Steps focus on 4 aha moments: Dispatch (parse rate cons), Copilot (AI ops),
  Load Board (rate negotiator), Refer & Earn (viral loop). Wrap ends with a
  clear CTA to the highest-value first action: "Add your first load".
*/

const STEPS = [
  {
    key: "welcome",
    icon: Sparkle,
    title: "Welcome to FreightPilot AI 🚛",
    body: "60-second tour so you know where the magic lives. Skip anytime with the X.",
    ctaText: "Show me",
    highlight: null,
  },
  {
    key: "dispatch",
    icon: Truck,
    title: "1. Paste rate cons → AI parses in 6 sec",
    body: "The Dispatch page reads any rate confirmation and pulls out lane, pay, stops, ref numbers. No more re-typing.",
    ctaText: "Next",
    highlight: '[data-testid="nav-dispatch"]',
  },
  {
    key: "loadboard",
    icon: CurrencyDollar,
    title: "2. AI drafts your counter-offers",
    body: "Paste a broker offer and your target rate — AI drafts the counter in seconds. You decide what to send.",
    ctaText: "Next",
    highlight: '[data-testid="nav-loadboard"]',
  },
  {
    key: "invoices",
    icon: ChartLineUp,
    title: "3. One-tap PDF invoices with pay links",
    body: "Every delivered load auto-generates a branded invoice. Email it to the broker in one click.",
    ctaText: "Next",
    highlight: '[data-testid="nav-invoices"]',
  },
  {
    key: "referrals",
    icon: Share,
    title: "4. Bring a fleet — both get +30 days free",
    body: "Every carrier you invite gets +30 days on their trial. You get +30 days too. Compounds fast.",
    ctaText: "Next",
    highlight: '[data-testid="nav-referrals"]',
  },
  {
    key: "finish",
    icon: Rocket,
    title: "You're all set. 🎯",
    body: "Fastest way to see FreightPilot in action: paste a rate con into Dispatch. Watch it work.",
    ctaText: "Add my first load →",
    highlight: null,
    goToOnClick: "/app/dispatch",
  },
];

export default function OnboardingTour() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  // Auto-play check on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await client.get("/onboarding/tour-status");
        if (mounted && data?.should_show) setVisible(true);
      } catch {/* silent — tour is optional */}
    })();
    return () => { mounted = false; };
  }, []);

  // Manual replay trigger — any component can dispatch `open-onboarding-tour`
  // (e.g. the sidebar "?" button) to re-open the tour on demand.
  useEffect(() => {
    const handler = () => {
      setStep(0);
      setVisible(true);
    };
    window.addEventListener("open-onboarding-tour", handler);
    return () => window.removeEventListener("open-onboarding-tour", handler);
  }, []);

  // Highlight target when step changes
  useEffect(() => {
    if (!visible) return;
    const target = STEPS[step]?.highlight;
    let el = null;
    if (target) {
      el = document.querySelector(target);
      if (el) el.classList.add("tour-highlight");
    }
    return () => {
      if (el) el.classList.remove("tour-highlight");
    };
  }, [visible, step]);

  // Keyboard nav
  useEffect(() => {
    if (!visible) return;
    const handler = (e) => {
      if (e.key === "Escape") skip();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, step]);

  const next = async () => {
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      await complete();
    }
  };
  const prev = () => step > 0 && setStep(step - 1);

  const skip = async () => {
    setBusy(true);
    try { await client.post("/onboarding/tour-dismiss"); } catch {}
    setVisible(false);
    setBusy(false);
  };

  const complete = async () => {
    setBusy(true);
    try { await client.post("/onboarding/tour-complete"); } catch {}
    setVisible(false);
    setBusy(false);
    const goto = STEPS[STEPS.length - 1].goToOnClick;
    if (goto) nav(goto);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const progressPct = ((step + 1) / STEPS.length) * 100;

  return (
    <>
      {/* Tour highlight ring style — added inline so no CSS import needed */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 41 !important;
          box-shadow: 0 0 0 3px rgba(255, 90, 31, 0.85), 0 0 24px rgba(255, 90, 31, 0.5) !important;
          border-radius: 10px;
          animation: tour-pulse 1.6s ease-in-out infinite;
        }
        @keyframes tour-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(255, 90, 31, 0.85), 0 0 24px rgba(255, 90, 31, 0.5); }
          50%      { box-shadow: 0 0 0 5px rgba(255, 90, 31, 1), 0 0 32px rgba(255, 90, 31, 0.7); }
        }
      `}</style>

      <div
        className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-50 w-[calc(100vw-2rem)] max-w-[380px] rounded-2xl border border-white/10 bg-[#0F0F0F] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        data-testid="onboarding-tour"
        role="dialog"
        aria-label="Product tour"
      >
        {/* Progress bar top */}
        <div className="h-1 bg-white/[0.04]">
          <div
            className="h-full bg-[#FF5A1F] transition-all duration-500 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
            Step {step + 1} of {STEPS.length}
          </div>
          <button
            onClick={skip}
            disabled={busy}
            data-testid="tour-skip-btn"
            className="text-neutral-500 hover:text-white p-1 -mr-1"
            title="Skip tour (Esc)"
          >
            <XIcon size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-2">
          <div className="w-11 h-11 rounded-lg bg-[#FF5A1F]/12 flex items-center justify-center mb-3">
            <Icon size={22} weight="fill" className="text-[#FF5A1F]" />
          </div>
          <h3 className="text-base sm:text-lg font-display font-semibold leading-tight text-white mb-2" data-testid={`tour-title-${current.key}`}>
            {current.title}
          </h3>
          <p className="text-sm text-neutral-400 leading-relaxed">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex items-center justify-between gap-3 border-t border-white/5 mt-2">
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === step ? "bg-[#FF5A1F]" : i < step ? "bg-white/40" : "bg-white/10"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={prev}
                disabled={busy}
                data-testid="tour-prev-btn"
                className="text-xs text-neutral-400 hover:text-white p-1.5 rounded-md inline-flex items-center gap-1"
              >
                <ArrowLeft size={12} weight="bold" /> Back
              </button>
            )}
            <button
              onClick={next}
              disabled={busy}
              data-testid="tour-next-btn"
              className="text-xs sm:text-sm font-semibold bg-[#FF5A1F] text-black rounded-full px-4 py-2 hover:bg-[#FF7038] transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {step === STEPS.length - 1 ? (
                <>{current.ctaText} <Rocket size={12} weight="fill" /></>
              ) : (
                <>{current.ctaText} <ArrowRight size={12} weight="bold" /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
